import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import Select from "react-select";
import QRCode from "qrcode.react";
import { Copy, Download, ExternalLink, Pencil, Trash2, Play, Pause } from "lucide-react";
import api from "../utils/api";
import { toLocalInputValue, localInputToISO } from "../utils/dates";

const OffersManagement = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    offerType: "percentage",
    discountValue: "",
    maxDiscountCap: "",
    minOrderValue: "",
    freeItemId: "",
    freeItemName: "",
    bogoAppliesTo: "all",
    bogoTargetIds: [],
    bogoTargetNames: [],
    startDate: "",
    endDate: "",
    isActive: true,
    showUpcoming: false
  });
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [userId, setUserId] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.user && decoded.user.id) {
          setUserId(decoded.user.id);
        }
      } catch (err) {
        console.error("Invalid token");
      }
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetchProducts();
    }
  }, [userId]);

  const fetchProducts = async () => {
    try {
      const response = await api.get(`get-product-data/${userId}`);
      setProducts(response.data || []);
      
      const catResponse = await api.get(`newcategories/${userId}?limit=1000`);
      setCategories(catResponse.data.data || catResponse.data || []);
    } catch (err) {
      console.error("Failed to fetch products/categories for offers", err);
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await api.get("/api/offers/admin");
      setOffers(response.data);
    } catch (error) {
      toast.error("Failed to fetch offers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value
    });
  };

  const handleEdit = (offer) => {
    setFormData({
      title: offer.title,
      description: offer.description || "",
      offerType: offer.offerType,
      discountValue: offer.discountValue || "",
      maxDiscountCap: offer.maxDiscountCap || "",
      minOrderValue: offer.minOrderValue || "",
      freeItemId: offer.freeItemId || "",
      freeItemName: offer.freeItemName || "",
      bogoAppliesTo: offer.bogoAppliesTo || "all",
      bogoTargetIds: offer.bogoTargetIds || [],
      bogoTargetNames: offer.bogoTargetNames || [],
      startDate: toLocalInputValue(offer.startDate),
      endDate: toLocalInputValue(offer.endDate),
      isActive: offer.isActive,
      showUpcoming: offer.showUpcoming || false
    });
    setEditingId(offer._id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      offerType: "percentage",
      discountValue: "",
      maxDiscountCap: "",
      minOrderValue: "",
      freeItemId: "",
      freeItemName: "",
      bogoAppliesTo: "all",
      bogoTargetIds: [],
      bogoTargetNames: [],
      startDate: "",
      endDate: "",
      isActive: true,
      showUpcoming: false
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // The picker gives local time without an offset; send exact instants so the
    // server doesn't read "18:00" as 18:00 UTC (23:30 in India)
    let submitData = {
      ...formData,
      startDate: localInputToISO(formData.startDate),
      endDate: localInputToISO(formData.endDate),
    };
    if (submitData.offerType === "free_item" && submitData.freeItemId) {
      const selectedProduct = products.find(p => p._id === submitData.freeItemId);
      if (selectedProduct) {
        submitData.freeItemName = selectedProduct.productName;
      }
    }
    
    if (submitData.offerType === "bogo") {
      submitData.bogoTargetNames = [];
      if (submitData.bogoAppliesTo === "product" && submitData.bogoTargetIds && submitData.bogoTargetIds.length > 0) {
        submitData.bogoTargetIds.forEach(id => {
          const prod = products.find(p => p._id === id);
          if (prod) submitData.bogoTargetNames.push(prod.productName);
        });
      } else if (submitData.bogoAppliesTo === "category" && submitData.bogoTargetIds && submitData.bogoTargetIds.length > 0) {
        submitData.bogoTargetIds.forEach(id => {
          const cat = categories.find(c => c._id === id);
          if (cat) submitData.bogoTargetNames.push(cat.newTitle || cat.category);
        });
      }
    }

    try {
      if (editingId) {
        await api.put(`/api/offers/${editingId}`, submitData);
        toast.success("Offer updated successfully!");
      } else {
        await api.post("/api/offers", submitData);
        toast.success("Offer created successfully!");
      }
      fetchOffers();
      resetForm();
    } catch (error) {
      toast.error(editingId ? "Failed to update offer" : "Failed to create offer");
    }
  };

  const copyToClipboard = () => {
    const publicLink = `${window.location.origin}/#/restaurant/${userId}/offers`;
    navigator.clipboard.writeText(publicLink);
    toast.success("Public link copied to clipboard!");
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById("public-offer-qr");
    if (canvas) {
      const pngUrl = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");
      let downloadLink = document.createElement("a");
      downloadLink.href = pngUrl;
      downloadLink.download = "offers_qr_code.png";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };

  const publicLink = `${window.location.origin}/#/restaurant/${userId}/offers`;

  const toggleStatus = async (id, currentStatus) => {
    try {
      await api.put(`/api/offers/${id}`, { isActive: !currentStatus });
      toast.success("Status updated");
      fetchOffers();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteOffer = async (id) => {
    if (!window.confirm("Are you sure you want to delete this offer?")) return;
    try {
      await api.delete(`/api/offers/${id}`);
      toast.success("Offer deleted");
      fetchOffers();
    } catch (error) {
      toast.error("Failed to delete offer");
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Offers Management</h2>
        <button
          onClick={() => {
            if (showForm) {
              resetForm();
            } else {
              setShowForm(true);
            }
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700"
        >
          {showForm ? "Cancel" : "+ Create New Offer"}
        </button>
      </div>

      {userId && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-sm border border-blue-100 mb-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Your Public Offers Page</h3>
            <p className="text-sm text-gray-600 mb-4">Share this link or print the QR code so your customers can see all active offers.</p>
            <div className="flex items-center gap-2">
              <a href={publicLink} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-md text-sm font-semibold text-blue-600 hover:bg-blue-50">
                <ExternalLink size={16} /> Open
              </a>
              <button onClick={copyToClipboard} className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-md text-sm font-semibold text-gray-700 hover:bg-gray-50">
                <Copy size={16} /> Copy Link
              </button>
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 flex flex-col items-center">
            <QRCode id="public-offer-qr" value={publicLink} size={100} renderAs="canvas" />
            <button onClick={downloadQRCode} className="mt-2 flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800">
              <Download size={14} /> Download QR
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h3 className="text-xl font-semibold mb-4">{editingId ? "Edit Offer" : "Create Offer"}</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Offer Title</label>
              <input type="text" name="title" value={formData.title} onChange={handleInputChange} required className="w-full p-2 border rounded" placeholder="e.g., Weekend 20% Off" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Offer Type</label>
              <select name="offerType" value={formData.offerType} onChange={handleInputChange} className="w-full p-2 border rounded">
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Flat Amount</option>
                <option value="bogo">Buy 1 Get 1</option>
                <option value="free_item">Free Item</option>
              </select>
            </div>
            
            {formData.offerType === "bogo" && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">BOGO Applies To</label>
                  <select name="bogoAppliesTo" value={formData.bogoAppliesTo} onChange={handleInputChange} className="w-full p-2 border rounded">
                    <option value="all">Entire Menu</option>
                    <option value="category">Specific Category</option>
                    <option value="product">Specific Product</option>
                  </select>
                </div>
                {formData.bogoAppliesTo === "category" && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Select Categories</label>
                    <Select
                      isMulti
                      options={categories.map(c => ({ value: c._id, label: c.newTitle || c.category }))}
                      value={categories.filter(c => formData.bogoTargetIds.includes(c._id)).map(c => ({ value: c._id, label: c.newTitle || c.category }))}
                      onChange={(selected) => setFormData({ ...formData, bogoTargetIds: selected ? selected.map(s => s.value) : [] })}
                      isClearable
                      isSearchable
                      placeholder="Search and select categories..."
                      className="text-sm"
                    />
                  </div>
                )}
                {formData.bogoAppliesTo === "product" && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Select Products</label>
                    <Select
                      isMulti
                      options={products.map(p => ({ value: p._id, label: p.productName }))}
                      value={products.filter(p => formData.bogoTargetIds.includes(p._id)).map(p => ({ value: p._id, label: p.productName }))}
                      onChange={(selected) => setFormData({ ...formData, bogoTargetIds: selected ? selected.map(s => s.value) : [] })}
                      isClearable
                      isSearchable
                      placeholder="Search and select products..."
                      className="text-sm"
                    />
                  </div>
                )}
              </>
            )}
            
            {formData.offerType === "free_item" ? (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Select Free Item</label>
                <Select
                  options={products.map(p => ({ value: p._id, label: `${p.productName} (${p.price} Rs.)` }))}
                  value={products.filter(p => p._id === formData.freeItemId).map(p => ({ value: p._id, label: `${p.productName} (${p.price} Rs.)` }))[0] || null}
                  onChange={(selected) => setFormData({ ...formData, freeItemId: selected ? selected.value : "" })}
                  isClearable
                  isSearchable
                  placeholder="Search products..."
                  className="text-sm"
                />
              </div>
            ) : formData.offerType === "bogo" ? null : (
              <div>
                <label className="block text-sm font-medium mb-1">Discount Value</label>
                <input type="number" name="discountValue" value={formData.discountValue} onChange={handleInputChange} required className="w-full p-2 border rounded" min="0" />
              </div>
            )}
            
            {formData.offerType === "percentage" && (
              <div>
                <label className="block text-sm font-medium mb-1">Max Discount Cap (₹) - Optional</label>
                <input type="number" name="maxDiscountCap" value={formData.maxDiscountCap} onChange={handleInputChange} className="w-full p-2 border rounded" min="0" />
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium mb-1">Min Order Value (₹) - Optional</label>
              <input type="number" name="minOrderValue" value={formData.minOrderValue} onChange={handleInputChange} className="w-full p-2 border rounded" min="0" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Start Date</label>
              <input type="datetime-local" name="startDate" value={formData.startDate} onChange={handleInputChange} required className="w-full p-2 border rounded" />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">End Date</label>
              <input type="datetime-local" name="endDate" value={formData.endDate} onChange={handleInputChange} required className="w-full p-2 border rounded" />
            </div>
            
            <div className="md:col-span-2 mt-2 p-3 bg-indigo-50 border border-indigo-100 rounded-lg flex items-start gap-3">
              <input 
                type="checkbox" 
                name="showUpcoming" 
                checked={formData.showUpcoming} 
                onChange={(e) => setFormData({ ...formData, showUpcoming: e.target.checked })}
                className="mt-1 w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
              />
              <div>
                <label className="block text-sm font-semibold text-indigo-900 mb-1">Show on Public Page Before Start Date</label>
                <p className="text-xs text-indigo-700">If checked, this offer will be visible to customers with a live countdown timer until it becomes active.</p>
              </div>
            </div>

            <div className="md:col-span-2 flex justify-end gap-3 mt-4">
              <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">{editingId ? "Update Offer" : "Save Offer"}</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p>Loading offers...</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type / Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Validity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {offers.map((offer) => (
                <tr key={offer._id}>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{offer.title}</div>
                    <div className="text-sm text-gray-500">{offer.description}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="uppercase text-xs font-semibold">{offer.offerType}</span>
                    <div className="text-sm">
                      {offer.offerType === "free_item" && `Free: ${offer.freeItemName || 'Item'}`}
                      {offer.offerType === "bogo" && `On: ${offer.bogoAppliesTo === 'all' ? 'All Items' : (offer.bogoTargetNames && offer.bogoTargetNames.length > 0 ? offer.bogoTargetNames.join(", ") : offer.bogoAppliesTo)}`}
                      {offer.offerType !== "free_item" && offer.offerType !== "bogo" && `Value: ${offer.discountValue}`}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {new Date(offer.startDate).toLocaleDateString()} - {new Date(offer.endDate).toLocaleDateString()}
                  </td>
                  <td className="p-3 border-b">
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => handleEdit(offer)}
                        className="p-2 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 transition-colors"
                        title="Edit Offer"
                      >
                        <Pencil size={16} />
                      </button>
                      <button 
                        onClick={() => toggleStatus(offer._id, offer.isActive)}
                        className={`p-2 rounded-full transition-colors ${offer.isActive ? "bg-amber-50 text-amber-600 hover:bg-amber-100" : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}
                        title={offer.isActive ? "Pause Offer" : "Activate Offer"}
                      >
                        {offer.isActive ? <Pause size={16} /> : <Play size={16} />}
                      </button>
                      <button 
                        onClick={() => deleteOffer(offer._id)}
                        className="p-2 bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition-colors"
                        title="Delete Offer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {offers.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No offers found. Create one to get started!</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OffersManagement;
